import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import _ from 'lodash';

import Fab from '@material-ui/core/Fab';

import {
    ProviderHeader,
    StatusButton,
    ActionPlusIcon,
    PopupMenu,
    SelectProvider,
} from '../../components/index';

import TodaySession from '../../components/Provider/Appointment/TodaySession';
import GroupSession from '../../components/Provider/Appointment/GroupSession';
import OneOnOneSession from '../../components/Provider/Appointment/OneOnOneSession';
import OpenSession from '../../components/Provider/Appointment/OpenSession';
import TrackReport from '../../components/Provider/Appointment/TrackReport';

import { providerPageWidth } from '../../constant';

import { alertError } from '../../redux/stateActions';
import { NEXT_MEETS_COUNT, REPORT_COUNT, GET_PROVIDERS } from '../../services/provider';

const Session = ({ history, location }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const actionButtonRef = useRef(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedTab, setSelectedTab] = useState(location.state?.tab || 0);

    useEffect(() => {
        if (location.state && !_.isUndefined(location.state.tab)) {
            setSelectedTab(location.state.tab);
        }
    }, [location]);

    const tabsChangeHandler = (value) => {
        setSelectedTab(value);
    };

    const handleClick = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    // hide picker if click somewhere else
    const handleClickOutside = (event) => {
        if (actionButtonRef.current && !actionButtonRef.current.contains(event.target)) {
            setAnchorEl(false);
        }
    };
    useEffect(() => {
        document.addEventListener('click', handleClickOutside, false);
        return () => {
            document.removeEventListener('click', handleClickOutside, false);
        };
    }, []);

    /*--------------- Staff List ---------------*/
    const [selectedStaff, setSelectedStaff] = useState('');
    const [staffOptions, setStaffOptions] = useState([]);

    const {
        error: getStaffError,
        data: getStaffData,
        refetch: getStaffRefetch,
    } = useQuery(GET_PROVIDERS, {
        variables: { id: user.staff.hospital.id },
        skip: _.isEmpty(user) || user.staff.staff_role.name === 'Provider',
    });

    useEffect(() => {
        getStaffRefetch();
    }, []);

    useEffect(() => {
        if (
            !_.isEmpty(user) &&
            !user.staff.deactivated &&
            ['Provider', 'Provider Admin'].includes(user.staff.staff_role.name)
        ) {
            setSelectedStaff(user.staff.id);
        }
    }, [user]);

    useEffect(() => {
        if (getStaffError) {
            dispatch(alertError(`Cannot Get Staff Data: ${getStaffError.message}`));
            return;
        }

        if (getStaffData) {
            const filtered = getStaffData.staffs.filter((s) => s.user !== null);

            const newStaffOptions = filtered.map((staff) => ({
                value: staff.id,
                text: staff?.user?.fullname,
                avatar: staff?.user?.avatar?.url,
            }));

            setStaffOptions(newStaffOptions);
        }
    }, [getStaffError, getStaffData]);

    /*--------------- Variables ---------------*/
    const [today] = useState(dayjs().format('YYYY-MM-DD'));

    const variables = useMemo(() => {
        const newVariables = {
            today: today,
            hospital: user.staff.hospital.id,
        };
        if (selectedStaff !== '') {
            newVariables.staff = selectedStaff;
        }
        return newVariables;
    }, [today, selectedStaff]);

    /*--------------- Get Next Meet Count ---------------*/
    const [sessionCount, setSessionCount] = useState(0);
    const [groupCount, setGroupCount] = useState(0);
    const [oneOnOneCount, setOneOnOneCount] = useState(0);
    const [openCount, setOpenCount] = useState(0);

    const { data: getNextMeetCountData, error: getNextMeetCountError } = useQuery(
        NEXT_MEETS_COUNT,
        {
            variables: variables,
            skip: _.isEmpty(user) || !today,
            fetchPolicy: 'network-only',
        }
    );

    useEffect(() => {
        if (getNextMeetCountError) {
            dispatch(alertError(`Cannot get count data: ${getNextMeetCountError.message}`));
        }

        if (getNextMeetCountData) {
            setSessionCount(getNextMeetCountData.allCount.aggregate.count);
            setGroupCount(getNextMeetCountData.groupCount.aggregate.count);
            setOneOnOneCount(getNextMeetCountData.oneCount.aggregate.count);
            setOpenCount(getNextMeetCountData.openCount.aggregate.count);
        }
    }, [getNextMeetCountError, getNextMeetCountData]);

    /*--------------- Get Report Session Count ---------------*/
    // Report will not includes open session, since open session won't be record any data
    const [reportCount, setReportCount] = useState(0);

    const { data: getReportCountData, error: getReportCountError } = useQuery(REPORT_COUNT, {
        variables: variables,
        skip: _.isEmpty(user) || !today,
    });

    useEffect(() => {
        if (getReportCountError) {
            dispatch(alertError(`Cannot get Open session count: ${getReportCountError.message}`));
        }

        if (getReportCountData) {
            setReportCount(getReportCountData.sessionsConnection.aggregate.count);
        }
    }, [getReportCountError, getReportCountData]);

    const onCreateGroupSession = () => {
        history.push(`/provider/calendar/group/add`);
    };
    const onCreateOneOnOneSession = () => {
        history.push(`/provider/calendar/one/add`);
    };
    const onCreateOpenSession = () => {
        history.push(`/provider/calendar/open/add`);
    };

    return (
        <div
            style={{
                padding: '15px 20px 50px',
                maxWidth: providerPageWidth,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '15px',
                position: 'relative',
            }}
        >
            <ProviderHeader>
                <div style={{ display: 'flex', columnGap: '15px' }}>
                    <StatusButton
                        name="Session"
                        $fontSize="24px"
                        value={sessionCount}
                        $tagColor="black"
                        onClick={() => tabsChangeHandler(0)}
                    />
                    <StatusButton
                        name="Group"
                        $color="gray2"
                        value={groupCount}
                        $tagColor="purple"
                        onClick={() => tabsChangeHandler(1)}
                    />
                    <StatusButton
                        name="1 on 1"
                        $color="gray2"
                        value={oneOnOneCount}
                        $tagColor="pink"
                        onClick={() => tabsChangeHandler(2)}
                    />
                    <StatusButton
                        name="Open"
                        $color="gray2"
                        value={openCount}
                        $tagColor="green"
                        onClick={() => tabsChangeHandler(3)}
                    />
                    <StatusButton
                        name="Track Report"
                        $color="gray2"
                        value={reportCount}
                        $tagColor="blue6"
                        onClick={() => tabsChangeHandler(4)}
                    />
                </div>
            </ProviderHeader>

            {['Admin', 'Provider Admin'].includes(user.staff.staff_role.name) &&
                selectedTab === 0 && (
                    <div style={{ width: '345px' }}>
                        <SelectProvider
                            staffOptions={staffOptions}
                            selectedStaff={selectedStaff}
                            setSelectedStaff={setSelectedStaff}
                        />
                    </div>
                )}

            {selectedTab === 0 && <TodaySession selectedStaff={selectedStaff} />}
            {selectedTab === 1 && (
                <GroupSession
                    staffOptions={staffOptions}
                    selectedStaff={selectedStaff}
                    setSelectedStaff={setSelectedStaff}
                />
            )}
            {selectedTab === 2 && (
                <OneOnOneSession
                    staffOptions={staffOptions}
                    selectedStaff={selectedStaff}
                    setSelectedStaff={setSelectedStaff}
                />
            )}
            {selectedTab === 3 && (
                <OpenSession
                    staffOptions={staffOptions}
                    selectedStaff={selectedStaff}
                    setSelectedStaff={setSelectedStaff}
                />
            )}
            {selectedTab === 4 && (
                <TrackReport
                    staffOptions={staffOptions}
                    selectedStaff={selectedStaff}
                    setSelectedStaff={setSelectedStaff}
                />
            )}

            <div style={{ position: 'fixed', right: '30px', bottom: '30px', zIndex: '100' }}>
                <PopupMenu
                    open={open}
                    top="-105px"
                    left="-165px"
                    onClose={handleClose}
                    disabled={user.staff.staff_role.name === 'Provider' && user.staff.deactivated}
                    options={[
                        {
                            text: 'Group Session',
                            onClick: onCreateGroupSession,
                        },
                        {
                            text: '1 on 1 Session',
                            onClick: onCreateOneOnOneSession,
                        },
                        {
                            text: 'Open Session',
                            onClick: onCreateOpenSession,
                        },
                    ]}
                />
                <Fab
                    aria-label="add"
                    style={{
                        width: '63px',
                        height: '63px',
                        background:
                            user.staff.staff_role.name === 'Provider' && user.staff.deactivated
                                ? 'rgba(0, 0, 0, 0.12)'
                                : 'white',
                        pointer:
                            user.staff.staff_role.name === 'Provider' && user.staff.deactivated
                                ? 'not-allowed'
                                : 'pointer',
                        boxShadow: '1px 6px 13px rgba(95, 102, 140, 0.2)',
                    }}
                    onClick={handleClick}
                    ref={actionButtonRef}
                    disabled={user.staff.staff_role.name === 'Provider' && user.staff.deactivated}
                >
                    <div
                        style={{
                            transform: 'scale(1, 1)',
                            paddingLeft: '5px',
                            paddingTop: '14px',
                        }}
                    >
                        <ActionPlusIcon />
                    </div>
                </Fab>
            </div>
        </div>
    );
};

export default Session;

Session.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
};
